import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Camera, Loader2, QrCode, Search, X } from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Modal de scan QR / code-barres (US-EQ-03).
 *
 * Stratégie :
 *  1. Si le navigateur expose `window.BarcodeDetector` (Chrome / Edge), on
 *     ouvre la caméra et on scanne en boucle. À chaque détection on appelle
 *     `/equipements/scan?code=…`. Si trouvé, on remonte l'équipement au parent.
 *  2. Si non supporté (Firefox / Safari < 17), on bascule sur une saisie
 *     manuelle. Le code peut être tapé OU collé (depuis une douchette USB qui
 *     se comporte en clavier).
 *
 * NOTE : la lib `html5-qrcode` n'est pas installée. Pour activer le scan vidéo
 * sur les navigateurs sans BarcodeDetector, l'ajouter aux deps et brancher ici.
 */

interface EquipementHit {
  id: string;
  code_equipement: string;
  designation: string;
}

// Types minimaux pour BarcodeDetector (pas dans lib.dom encore).
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): {
    detect(source: HTMLVideoElement | ImageBitmap): Promise<DetectedBarcode[]>;
  };
  getSupportedFormats?(): Promise<string[]>;
}

export function ScanQrModal({
  onClose,
  onFound,
}: {
  onClose(): void;
  onFound(equipement: EquipementHit): void;
}) {
  const [mode, setMode] = useState<'auto' | 'manual'>('auto');
  const [manualCode, setManualCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [notFound, setNotFound] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stoppedRef = useRef(false);

  const hasBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  // Démarre la caméra si supporté.
  useEffect(() => {
    if (mode !== 'auto' || !hasBarcodeDetector) {
      setMode('manual');
      return;
    }
    let cancelled = false;
    stoppedRef.current = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }

        const Ctor = (window as unknown as { BarcodeDetector: BarcodeDetectorCtor })
          .BarcodeDetector;
        const detector = new Ctor({
          formats: ['qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8'],
        });

        async function tick() {
          if (stoppedRef.current || cancelled) return;
          const video = videoRef.current;
          if (video && video.readyState >= 2) {
            try {
              const results = await detector.detect(video);
              const first = results[0];
              if (first && first.rawValue) {
                const code = first.rawValue;
                stoppedRef.current = true;
                stopCamera();
                await tryScan(code);
                return;
              }
            } catch {
              // Silencieux — on retentera au prochain tick.
            }
          }
          requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      } catch {
        setMode('manual');
      }
    })();

    return () => {
      cancelled = true;
      stoppedRef.current = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  async function tryScan(code: string) {
    setBusy(true);
    setErrorMessage(null);
    setNotFound(null);
    try {
      const res = await api.get<{ data: EquipementHit }>(
        `/equipements/scan?code=${encodeURIComponent(code)}`,
      );
      onFound(res.data.data);
    } catch (err) {
      const msg = extractErrorMessage(err, 'Code non reconnu');
      setNotFound(code);
      setErrorMessage(msg);
      // Permet de retenter en mode caméra.
      if (mode === 'auto') {
        stoppedRef.current = false;
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!manualCode.trim()) return;
    await tryScan(manualCode.trim());
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <QrCode className="w-5 h-5 mr-2 text-primary-600" />
              Scanner un équipement
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {errorMessage && notFound && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-orange-800">Code non reconnu</p>
                  <p className="text-orange-700">
                    "{notFound}" n'est lié à aucun équipement de ce tenant. Réessayez.
                  </p>
                </div>
              </div>
            )}

            {mode === 'auto' && hasBarcodeDetector ? (
              <>
                <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                  <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
                  <div className="absolute inset-0 border-4 border-primary-400 rounded-lg pointer-events-none opacity-50" />
                  {busy && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 text-center flex items-center justify-center">
                  <Camera className="w-3.5 h-3.5 mr-1" />
                  Pointez la caméra vers l'étiquette QR / code-barres.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    stopCamera();
                    stoppedRef.current = true;
                    setMode('manual');
                  }}
                  className="w-full text-xs text-primary-600 hover:text-primary-700"
                >
                  Saisir le code manuellement
                </button>
              </>
            ) : (
              <form onSubmit={handleManualSubmit} className="space-y-3">
                {!hasBarcodeDetector && (
                  <p className="text-xs text-gray-500">
                    Votre navigateur ne supporte pas le scan caméra. Saisissez ou collez le code de
                    l'équipement (code, numéro d'inventaire, code-barres).
                  </p>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    autoFocus
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Code, n° inventaire, code-barres…"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy || !manualCode.trim()}
                  className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Rechercher
                </button>
                {hasBarcodeDetector && (
                  <button
                    type="button"
                    onClick={() => setMode('auto')}
                    className="w-full text-xs text-primary-600 hover:text-primary-700"
                  >
                    <Camera className="w-3.5 h-3.5 inline mr-1" />
                    Revenir au mode caméra
                  </button>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
